'use client';

import type { FragmentType } from 'src/gql';
import { useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@savvy-kit/components/empty';
import { ClipboardCheckIcon } from 'lucide-react';
import { unmaskFragment } from 'src/gql';

import { TaxEnrollmentDialog } from './tax-enrollment-dialog';
import { TaxEnrollmentFormFragment } from './tax-enrollment-form';
import { useTaxEnrollmentActions } from './tax-enrollment-mutations';
import { CURRENT_TAX_YEAR } from './tax-utils';

type TaxEnrollmentProps = {
  readonly householdFrag: FragmentType<typeof TaxEnrollmentFormFragment>;
  readonly loading: boolean;
};

export function TaxEnrollment({ householdFrag, loading }: TaxEnrollmentProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { createNewEnrollments } = useTaxEnrollmentActions();
  const household = unmaskFragment(TaxEnrollmentFormFragment, householdFrag);

  const handleNotInterested = async () => {
    await createNewEnrollments(
      {
        enrollmentType: 'other',
        billingAmount: null,
        coClientEnrollmentType: 'other',
        coClientBillingAmount: null,
        isLateFiling: false,
      },
      household,
    );
  };

  return (
    <>
      <Empty className="flex p-6 flex-col items-center rounded-xl border border-solid border-border-light w-full bg-white">
        <EmptyMedia
          variant="icon"
          className="flex w-12 h-12 p-2 m-0 justify-center items-center border border-base-border shadow-xs rounded-md bg-base-card"
        >
          <ClipboardCheckIcon size={24} className="text-base-foreground" />
        </EmptyMedia>
        <EmptyHeader className="flex flex-col items-center w-full max-w-full gap-2 text-center">
          <EmptyTitle className="text-[#141A26] text-xl font-semibold leading-7">
            Tax filing {CURRENT_TAX_YEAR}
          </EmptyTitle>
          <EmptyDescription className="text-slate-500 text-base leading-6">
            Enroll households in Savvy's tax filing service or indicate they're using an external CPA. Either way, keep
            track of your clients' tax status in one place.
          </EmptyDescription>
        </EmptyHeader>
        <div className="flex gap-3">
          <Button variant="default" onClick={() => setIsDialogOpen(true)}>
            Register household
          </Button>
          <Button variant="secondary" onClick={handleNotInterested}>
            Mark not interested
          </Button>
        </div>
      </Empty>
      <TaxEnrollmentDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        householdFrag={householdFrag}
        loading={loading}
      />
    </>
  );
}
